/**
 * PageHelpDialog.tsx — the interactive page guide opened by PageHelpButton.
 *
 * Renders a HelpContent definition (components/help/content/) in three tabs:
 *   Overview             — hero illustration + short plain-language intro
 *   What's on this page  — HelpMiniMock snapshot with numbered markers +
 *                          per-region explanations and live example chips
 *   How it all connects  — HelpFlowDiagram chain with clickable navigation
 *                          chips (the dialog closes before navigating)
 *
 * Content-agnostic: all copy, geometry, and links come from the definition.
 * Theme tokens only — no hex literals.
 */

import { useState } from 'react';
import {
  Box, Dialog, DialogContent, IconButton, List, ListItemButton, Tab, Tabs,
  Typography, alpha, useTheme,
} from '@mui/material';
import { Close, HelpOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { HelpContent, HelpSnapshotItem } from './helpTypes';
import { HelpMiniMock } from './HelpMiniMock';
import { HelpFlowDiagram } from './HelpFlowDiagram';

// ── Snapshot item (right-hand list entry) ─────────────────────────────────────

const SnapshotItemRow = ({
  item, number, selected, onSelect,
}: {
  item: HelpSnapshotItem;
  number: number;
  selected: boolean;
  onSelect: () => void;
}) => {
  const theme = useTheme();
  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        borderRadius: 1.5,
        mb: 0.5,
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.5,
        border: `1px solid ${selected ? theme.palette.primary.main : theme.border.color}`,
        '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.72rem', fontWeight: 700,
          border: `2px solid ${theme.palette.primary.main}`,
          color: selected ? theme.palette.primary.contrastText : theme.palette.primary.main,
          backgroundColor: selected ? theme.palette.primary.main : 'transparent',
        }}>
          {number}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
      </Box>
      {selected && (
        <Box sx={{ pl: 3.75 }}>
          <Box component="ul" sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {item.points.map((point) => (
              <Typography key={point} component="li" variant="body2"
                sx={{ color: theme.palette.text.secondary, lineHeight: 1.45 }}>
                {point}
              </Typography>
            ))}
          </Box>
          {item.example && (
            <Box sx={{ mt: 1 }}>
              {item.exampleCaption && (
                <Typography variant="caption" sx={{
                  display: 'block', mb: 0.5, fontWeight: 700,
                  color: theme.palette.text.secondary,
                }}>
                  {item.exampleCaption}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {item.example}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </ListItemButton>
  );
};

// ── The dialog ────────────────────────────────────────────────────────────────

interface PageHelpDialogProps {
  open: boolean;
  onClose: () => void;
  content: HelpContent;
}

export const PageHelpDialog = ({ open, onClose, content }: PageHelpDialogProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [selectedId, setSelectedId] = useState(content.snapshot.items[0]?.id ?? '');

  const numberById: Record<string, number> = {};
  content.snapshot.items.forEach((item, i) => { numberById[item.id] = i + 1; });

  const handleNavigate = (route: string) => {
    onClose();
    navigate(route);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, minHeight: 560 } } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, pb: 0.5,
      }}>
        <HelpOutline sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', flex: 1 }}>
          {content.title}
        </Typography>
        <IconButton aria-label="Close the guide" onClick={onClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: number) => setTab(v)}
        sx={{ px: 3, borderBottom: `1px solid ${theme.border.color}`, minHeight: 40 }}
      >
        <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
        <Tab label="What's on this page" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
        <Tab label="How it all connects" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
      </Tabs>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component="img"
              src={content.heroSrc}
              alt={content.heroAlt}
              sx={{
                width: '100%',
                maxHeight: 280,
                objectFit: 'cover',
                borderRadius: 2,
                border: `1px solid ${theme.border.color}`,
              }}
            />
            {content.overview.map((paragraph) => (
              <Typography key={paragraph.slice(0, 40)} variant="body1"
                sx={{ lineHeight: 1.6, maxWidth: 860 }}>
                {paragraph}
              </Typography>
            ))}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {content.snapshot.intro}
            </Typography>
            <Box sx={{
              display: 'flex', gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
            }}>
              <Box sx={{ flex: '1.15 1 0', width: '100%', position: 'sticky', top: 0 }}>
                <HelpMiniMock
                  areas={content.snapshot.areas}
                  numberById={numberById}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              </Box>
              <List dense disablePadding sx={{ flex: '1 1 0', width: '100%' }}>
                {content.snapshot.items.map((item, i) => (
                  <SnapshotItemRow
                    key={item.id}
                    item={item}
                    number={i + 1}
                    selected={item.id === selectedId}
                    onSelect={() => setSelectedId(item.id)}
                  />
                ))}
              </List>
            </Box>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1, lineHeight: 1.6 }}>
                {content.connections.intro}
              </Typography>
              <Box
                component="img"
                src={content.connections.imageSrc}
                alt=""
                aria-hidden
                sx={{
                  width: 200,
                  borderRadius: 2,
                  border: `1px solid ${theme.border.color}`,
                  opacity: 0.9,
                  display: { xs: 'none', sm: 'block' },
                }}
              />
            </Box>
            <HelpFlowDiagram stages={content.connections.stages} onNavigate={handleNavigate} />
            {content.connections.footnote && (
              <Typography variant="body2" sx={{
                color: theme.palette.text.secondary,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.admin.mappingUnconfirmed, 0.08),
                border: `1px solid ${alpha(theme.admin.mappingUnconfirmed, 0.35)}`,
              }}>
                {content.connections.footnote}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
